import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, FileText, Megaphone, Sparkles, BookOpen } from "lucide-react";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";
import ConvergenLogo from "../components/ConvergenMark";
import CountUp from "../components/CountUp";
import WhitePaperCapture from "../components/WhitePaperCapture";
import { useHomeContent, pick } from "../lib/useHomeContent";

const INSIGHT_ICONS = {
  "white-paper": FileText,
  press: Megaphone,
  case: Sparkles,
  consensus: BookOpen,
};
const INSIGHT_LABELS_ES = {
  "white-paper": "White Paper",
  press: "Comunicado",
  case: "Caso",
  consensus: "Consenso",
};
const INSIGHT_LABELS_EN = {
  "white-paper": "White Paper",
  press: "Press",
  case: "Case",
  consensus: "Consensus",
};

export default function Home() {
  const { t, lang } = useLang();
  const { content } = useHomeContent();

  // ----- Resolve every text from CMS, with i18n fallback -----
  const hero = content?.hero || {};
  const ch = content?.challenge || {};
  const ev = content?.evolution || {};
  const ms = content?.mosaic_section || {};
  const ind = content?.indicators_section || {};
  const indu = content?.industries_section || {};
  const ins = content?.insights_section || {};

  // Hero
  const heroEyebrow = pick(hero, "eyebrow", lang, t.home.eyebrow);
  const heroTitle1 = pick(hero, "title1", lang, t.home.hero_title_1);
  const heroTitle2 = pick(hero, "title2", lang, t.home.hero_title_2);
  const heroSub = pick(hero, "sub", lang, t.home.hero_sub);
  const heroCta = pick(hero, "cta_primary", lang, t.home.hero_cta);
  const heroCta2 = pick(hero, "cta_secondary", lang, t.home.hero_secondary);

  // Challenge
  const chEyebrow = pick(ch, "eyebrow", lang, t.home.challenge_eyebrow);
  const chTitle = pick(ch, "title", lang, t.home.challenge_title);
  const chBody = pick(ch, "body", lang, t.home.challenge_p1);
  const chExtra = pick(content || {}, "challenge_extra", lang, t.home.challenge_p2);

  // Evolution
  const evEyebrow = pick(ev, "eyebrow", lang, t.home.evolution_eyebrow);
  const evTitle = pick(ev, "title", lang, t.home.evolution_title);
  const evBody = pick(ev, "body", lang, t.home.evolution_p);
  const evFrom = pick(content || {}, "evolution_from", lang, t.home.from_label);
  const evTo = pick(content || {}, "evolution_to", lang, t.home.to_label);

  // KPIs section
  const indEyebrow = pick(ind, "eyebrow", lang, t.indicators.eyebrow);
  const indTitle = pick(ind, "title", lang, t.indicators.title);
  const indBody = pick(ind, "body", lang, t.indicators.sub);
  const indDisclaimer = pick(content || {}, "indicators_disclaimer", lang, t.indicators.disclaimer);

  // Indicators items — empty array means "section visible but no cards rendered until CMS adds KPIs"
  const indicators = content?.indicators
    ? content.indicators.map((it) => ({
        value: it.value,
        decimals: it.decimals,
        suffix: it.suffix,
        prefix: it.prefix,
        label: lang === "en" ? it.label_en : it.label_es,
      }))
    : t.indicators.items;

  // Industries section
  const induEyebrow = pick(indu, "eyebrow", lang, t.industries.eyebrow);
  const induTitle = pick(indu, "title", lang, t.industries.title);
  const induBody = pick(indu, "body", lang, t.industries.sub);
  const induNote = pick(content || {}, "industries_note", lang, t.industries.note);
  const industries = (content?.industries && content.industries.length > 0)
    ? content.industries.map((it) => ({
        count: it.count,
        title: lang === "en" ? it.title_en : it.title_es,
        desc: lang === "en" ? it.desc_en : it.desc_es,
      }))
    : t.industries.items;

  // Mosaic tiles
  const msEyebrow = pick(ms, "eyebrow", lang, t.home.mosaic_eyebrow);
  const msTitle = pick(ms, "title", lang, t.home.mosaic_title);
  const defaultMosaic = [
    { to: "/servicios", title: t.home.tile_consulting_title, desc: t.home.tile_consulting_desc, variant: "default", testid: "tile-consulting" },
    { to: "/iieas", title: t.home.tile_iieas_title, desc: t.home.tile_iieas_desc, variant: "academic", testid: "tile-iieas" },
    { to: "/synapse", title: t.home.tile_synapse_title, desc: t.home.tile_synapse_desc, variant: "tech", testid: "tile-synapse" },
  ];
  const mosaicItems = (content?.mosaic && content.mosaic.length === 3)
    ? content.mosaic.map((it, i) => ({
        ...defaultMosaic[i],
        title: lang === "en" ? it.title_en : it.title_es,
        desc: lang === "en" ? it.desc_en : it.desc_es,
      }))
    : defaultMosaic;

  // Insights
  const insEyebrow = pick(ins, "eyebrow", lang, "Pensamiento Convergen");
  const insTitle = pick(ins, "title", lang, "Insights y publicaciones recientes.");
  const insBody = pick(ins, "body", lang, "");
  const insights = (content?.insights || []).map((it) => ({
    type: it.type || "white-paper",
    date: it.date,
    title: lang === "en" ? it.title_en : it.title_es,
    summary: lang === "en" ? it.summary_en : it.summary_es,
    link: it.link,
  }));

  return (
    <div data-testid="page-home">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        {/* Discreet tech ambient layer */}
        <div aria-hidden className="absolute inset-0 tech-grid opacity-50 pointer-events-none" />
        <div aria-hidden className="ambient-orb subtle" style={{ top: "-180px", right: "-160px" }} />

        <div className="relative page-container pt-20 md:pt-28 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <FadeUp>
              <p className="eyebrow mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
                <span className="live-dot" aria-hidden />
                {heroEyebrow}
              </p>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h1 className="display-heading text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02]">
                {heroTitle1}<br />
                <span className="text-cerulean">{heroTitle2}</span>
              </h1>
              <span aria-hidden className="shimmer-rule mt-6 w-40 max-w-full" />
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mt-10 text-base md:text-lg text-text-muted max-w-2xl leading-relaxed" data-testid="hero-sub">
                {heroSub}
              </p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" data-testid="hero-cta">
                  <Button className="btn-sweep btn-sweep-light bg-ink text-white rounded-none h-12 px-7 text-[12px] tracking-[0.18em] uppercase font-medium">
                    {heroCta} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/quienes-somos" data-testid="hero-secondary">
                  <Button variant="outline" className="btn-sweep border-ink text-ink rounded-none h-12 px-7 text-[12px] tracking-[0.18em] uppercase font-medium">
                    {heroCta2}
                  </Button>
                </Link>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.3} className="lg:col-span-4 hidden lg:block">
            <div className="lift-card relative w-full aspect-square border border-rule p-8 bg-pearl">
              <div className="absolute top-3 left-3 text-[10px] font-mono tracking-widest text-cerulean">001 / EVIDENCE</div>
              <div className="absolute top-3 right-3 text-[10px] font-mono tracking-widest text-cerulean">002 / ACCESS</div>
              <div className="absolute bottom-3 left-3 text-[10px] font-mono tracking-widest text-cerulean">003 / OUTCOMES</div>
              <div className="absolute bottom-3 right-3 text-[10px] font-mono tracking-widest text-cerulean">004 / PATIENT</div>
              <div className="w-full h-full flex items-center justify-center">
                <ConvergenLogo size={280} />
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="page-container pb-6" />
      </section>

      <Divider />

      {/* ---------- CHALLENGE ---------- */}
      <section className="section-pad bg-pearl">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <p className="eyebrow mb-5">{chEyebrow}</p>
            <h2 className="display-heading text-3xl md:text-5xl accent-rule">{chTitle}</h2>
          </FadeUp>
          <div className="lg:col-span-8 space-y-6 text-text-muted text-base md:text-lg leading-relaxed">
            <FadeUp delay={0.1}><p>{chBody}</p></FadeUp>
            <FadeUp delay={0.18}><p>{chExtra}</p></FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- EVOLUTION ---------- */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow mb-5">{evEyebrow}</p>
            <h2 className="display-heading text-3xl md:text-5xl max-w-3xl accent-rule">{evTitle}</h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-10 text-text-muted text-base md:text-lg max-w-3xl leading-relaxed">{evBody}</p>
          </FadeUp>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeUp delay={0.15}>
              <div className="border border-rule p-10 h-full bg-white">
                <div className="mt-3 display-heading text-2xl text-text-muted line-through decoration-1">{evFrom}</div>
                <div className="mt-8 text-sm text-text-muted">
                  Inventarios desplazados · Volumen de unidades · Transacción comercial
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.25}>
              <div className="border border-cerulean p-10 h-full bg-white relative">
                <div className="absolute top-0 left-0 h-1 w-16 bg-cerulean" />
                <div className="mt-3 display-heading text-2xl text-ink">{evTo}</div>
                <div className="mt-8 text-sm text-text-muted">
                  Vidas impactadas · Progresión clínica evitada · Sustentabilidad para pagadores
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Divider />

      {/* ---------- INDICATORS ---------- */}
      <section className="section-pad bg-ink text-white">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow text-cerulean">{indEyebrow}</p>
            <h2 className="display-heading text-white text-3xl md:text-5xl max-w-3xl mt-5">{indTitle}</h2>
            <p className="mt-8 text-white/65 text-base md:text-lg max-w-2xl leading-relaxed">{indBody}</p>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15">
            {indicators.map((m, i) => (
              <StaggerItem key={i}>
                <div className="bg-ink p-7 md:p-10 h-full" data-testid={`indicator-${i + 1}`}>
                  <div className="text-[10px] font-mono tracking-[0.22em] text-cerulean">KPI {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-8 display-heading text-white text-4xl md:text-5xl lg:text-6xl flex items-baseline">
                    {m.prefix && <span className="mr-1 text-cerulean text-2xl md:text-3xl">{m.prefix}</span>}
                    <CountUp end={m.value} decimals={m.decimals || 0} suffix={m.suffix || ""} />
                  </div>
                  <div className="mt-6 h-px w-10 bg-cerulean/60" />
                  <p className="mt-6 text-sm text-white/65 leading-relaxed">{m.label}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          {indicators.length === 0 && content?.whitepaper?.enabled !== false && (
            <WhitePaperCapture whitepaper={content?.whitepaper} />
          )}

          {indicators.length > 0 && indDisclaimer && (
            <FadeUp delay={0.2}>
              <p className="mt-10 text-[11px] font-mono tracking-[0.22em] text-white/40 uppercase">{indDisclaimer}</p>
            </FadeUp>
          )}
        </div>
      </section>

      <Divider />

      {/* ---------- INDUSTRIES ---------- */}
      <section className="section-pad bg-white">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <p className="eyebrow mb-5">{induEyebrow}</p>
            <h2 className="display-heading text-3xl md:text-5xl accent-rule">{induTitle}</h2>
            <p className="mt-10 text-text-muted text-base leading-relaxed max-w-md">{induBody}</p>
            <p className="mt-8 text-[11px] font-mono tracking-[0.22em] uppercase text-cerulean">{induNote}</p>
          </FadeUp>

          <Stagger className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-rule">
            {industries.map((it, i) => (
              <StaggerItem key={i}>
                <div className="bg-white p-7 h-full mosaic-card" data-testid={`industry-${i + 1}`}>
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-mono tracking-[0.22em] text-cerulean">SEG.{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[10px] font-mono tracking-widest text-text-muted">{it.count}</span>
                  </div>
                  <h3 className="mt-8 display-heading text-lg md:text-xl leading-snug">{it.title}</h3>
                  <p className="mt-3 text-sm text-text-muted leading-relaxed">{it.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- INSIGHTS (Pensamiento Convergen) ---------- */}
      {insights.length > 0 && (
        <>
          <Divider />
          <section className="section-pad bg-pearl" data-testid="section-insights">
            <div className="page-container">
              <FadeUp>
                <p className="eyebrow mb-5">{insEyebrow}</p>
                <h2 className="display-heading text-3xl md:text-5xl max-w-3xl accent-rule">{insTitle}</h2>
                {insBody && (
                  <p className="mt-10 text-text-muted text-base md:text-lg max-w-3xl leading-relaxed">{insBody}</p>
                )}
              </FadeUp>

              <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                {insights.map((it, i) => {
                  const Icon = INSIGHT_ICONS[it.type] || FileText;
                  const typeLabel = (lang === "en" ? INSIGHT_LABELS_EN : INSIGHT_LABELS_ES)[it.type] || "Insight";
                  const Wrapper = it.link ? "a" : "div";
                  const wrapperProps = it.link
                    ? { href: it.link, target: "_blank", rel: "noopener noreferrer" }
                    : {};
                  return (
                    <StaggerItem key={i}>
                      <Wrapper
                        {...wrapperProps}
                        className="block bg-white border border-rule p-7 h-full mosaic-card group"
                        data-testid={`insight-${i + 1}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-cerulean" strokeWidth={1.5} />
                            <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-cerulean">{typeLabel}</span>
                          </div>
                        </div>
                        <h3 className="mt-8 display-heading text-lg md:text-xl leading-snug">{it.title}</h3>
                        <p className="mt-4 text-sm text-text-muted leading-relaxed">{it.summary}</p>
                        {it.link && (
                          <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase text-ink group-hover:text-cerulean transition-colors">
                            {lang === "en" ? "Read" : "Leer"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </div>
                        )}
                      </Wrapper>
                    </StaggerItem>
                  );
                })}
              </Stagger>
            </div>
          </section>
        </>
      )}

      <Divider />

      {/* ---------- MOSAIC ---------- */}
      <section className="section-pad bg-white">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow mb-5">{msEyebrow}</p>
            <h2 className="display-heading text-3xl md:text-5xl max-w-3xl accent-rule">{msTitle}</h2>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {mosaicItems.map((tile, i) => (
              <StaggerItem key={i}>
                <MosaicCard
                  to={tile.to}
                  label={`0${i + 1}`}
                  title={tile.title}
                  desc={tile.desc}
                  cta={t.home.explore}
                  testid={tile.testid}
                  variant={tile.variant}
                />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}

function MosaicCard({ to, label, title, desc, cta, testid, variant = "default" }) {
  const head =
    variant === "academic"
      ? "iieas-display text-3xl"
      : variant === "tech"
      ? "synapse-display text-2xl"
      : "display-heading text-2xl";
  return (
    <Link
      to={to}
      data-testid={testid}
      className="mosaic-card lift-card group block bg-white border border-rule p-8 md:p-10 h-full relative overflow-hidden"
    >
      {/* Animated cerulean accent bar that grows on hover */}
      <span
        aria-hidden
        className="absolute top-0 left-0 h-0.5 w-0 bg-cerulean transition-all duration-500 group-hover:w-full"
      />
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono tracking-[0.22em] text-cerulean">{label}</span>
        <ArrowRight className="w-5 h-5 text-ink group-hover:text-cerulean group-hover:translate-x-1 transition-all duration-300" strokeWidth={1.5} />
      </div>
      <h3 className={`mt-10 ${head}`}>{title}</h3>
      <p className="mt-5 text-sm text-text-muted leading-relaxed">{desc}</p>
      <div className="mt-10 inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase text-ink group-hover:text-cerulean transition-colors">
        {cta} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
      </div>
    </Link>
  );
}

function Divider() {
  return <div className="page-container"><div className="h-px bg-rule" /></div>;
}
